/**
 * Player Details Service
 *
 * Handles API calls for player details modal data.
 * Follows Single Responsibility Principle - only handles player detail fetching.
 */

import api from './api';

// Cache for player details to avoid redundant API calls
const cache = new Map();
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Get cached data if still valid
 */
function getCached(key) {
  const cached = cache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
    return cached.data;
  }
  cache.delete(key);
  return null;
}

/**
 * Set cache data
 */
function setCache(key, data) {
  cache.set(key, { data, timestamp: Date.now() });
}

/**
 * Player Details Service
 */
const playerDetailsService = {
  /**
   * Get comprehensive player details for the modal
   *
   * @param {string} sport - Sport type: 'nba', 'nfl', 'nhl'
   * @param {number} playerId - Internal player ID
   * @param {Object} options - Optional parameters
   * @param {number} options.gamesLimit - Number of recent games (default 10)
   * @param {boolean} options.includePredictions - Include AI predictions
   * @returns {Promise<Object>} Player details response
   */
  async getPlayerDetails(sport, playerId, options = {}) {
    const { gamesLimit = 10, includePredictions = true } = options;

    // Check cache first
    const cacheKey = `${sport}-${playerId}-${gamesLimit}`;
    const cached = getCached(cacheKey);
    if (cached) {
      return cached;
    }

    try {
      const response = await api.get(`/players/${sport}/${playerId}`, {
        params: {
          games_limit: gamesLimit,
          include_predictions: includePredictions,
        },
      });

      const data = response.data;
      setCache(cacheKey, data);
      return data;
    } catch (error) {
      console.error(`Error fetching player details for ${sport} player ${playerId}:`, error);
      throw error;
    }
  },

  /**
   * Get player details by ESPN ID
   * Useful when internal ID is not available
   *
   * @param {string} sport - Sport type
   * @param {string} espnPlayerId - ESPN player ID
   * @param {Object} options - Optional parameters
   * @returns {Promise<Object>} Player details response
   */
  async getPlayerDetailsByEspnId(sport, espnPlayerId, options = {}) {
    const { gamesLimit = 10, includePredictions = true } = options;

    try {
      const response = await api.get(`/players/${sport}/by-espn/${espnPlayerId}`, {
        params: {
          games_limit: gamesLimit,
          include_predictions: includePredictions,
        },
      });

      return response.data;
    } catch (error) {
      console.error(`Error fetching player by ESPN ID ${espnPlayerId}:`, error);
      throw error;
    }
  },

  /**
   * Clear the cache (useful for testing or force refresh)
   */
  clearCache() {
    cache.clear();
  },

  /**
   * Prefetch player details (fire and forget)
   * Call this when user hovers over a player row
   *
   * @param {string} sport - Sport type
   * @param {number} playerId - Player ID
   */
  prefetch(sport, playerId) {
    const cacheKey = `${sport}-${playerId}-10`;
    if (!getCached(cacheKey)) {
      // Fire and forget - don't await
      this.getPlayerDetails(sport, playerId).catch(() => {
        // Silently ignore prefetch errors
      });
    }
  },
};

export default playerDetailsService;
